import { useCallback } from 'react';
import { toast } from 'sonner';
import usePushNotifications from '@/hooks/usePushNotifications';

/**
 * Contextual push-notification nudge that fires right after a user
 * successfully publishes a listing (Sk8 Shop / Hire / Event).
 *
 * UX rules:
 *  - Only nudges if web-push is supported AND the user is NOT already
 *    subscribed (no nag for already-opted-in users).
 *  - Snoozes per-listing-type: once a user dismisses or accepts the
 *    prompt for `"sk8-shop"`, we don't show it again for that type for
 *    14 days. Other types still nudge independently.
 *  - Single sonner toast with an [Enable] action — no modal, no friction.
 *
 * Usage:
 *   const promptForPush = useListingPushPrompt();
 *   await createListing(...);
 *   promptForPush('sk8-shop'); // or 'event', 'hire'
 */
export default function useListingPushPrompt() {
  const { isSupported, isSubscribed, subscribe } = usePushNotifications();

  return useCallback((kind = 'sk8-shop') => {
    if (!isSupported || isSubscribed) return;

    const storageKey = `rollm8s_push_prompt_${kind}_snoozed_until`;
    const snoozedUntil = parseInt(localStorage.getItem(storageKey) || '0', 10);
    if (snoozedUntil && Date.now() < snoozedUntil) return;

    const snoozeFor14Days = () => {
      const fourteenDays = 14 * 24 * 60 * 60 * 1000;
      localStorage.setItem(storageKey, String(Date.now() + fourteenDays));
    };

    const titles = {
      'sk8-shop': 'Get notified the moment someone enquires',
      event:      'Get notified when riders book your event',
      hire:       'Get notified the moment someone books',
    };

    toast(titles[kind] || titles['sk8-shop'], {
      description: 'Turn on push notifications so you never miss a message.',
      duration: 10000,
      action: {
        label: 'Enable',
        onClick: async () => {
          try {
            const ok = await subscribe();
            if (ok) toast.success('Notifications on!');
            else toast.error('Could not enable push. Check your browser settings.');
          } catch (e) {
            toast.error(`Setup failed: ${e?.message || 'Unknown error'}`);
          } finally {
            snoozeFor14Days();
          }
        },
      },
      cancel: {
        label: 'Not now',
        onClick: snoozeFor14Days,
      },
    });
  }, [isSupported, isSubscribed, subscribe]);
}
